<template>
  <template v-if="currentDocument">
    <div class="pa-0 text-black mb-4">
      {{ i18n.t('texts.forward_text_hint') }}
    </div>

    <v-form ref="forwardForm" @submit.prevent="sendInvitations">
      <div
        v-for="(email, index) in emails"
        :key="`email-input-${index}`"
        class="d-flex align-center justify-center ga-2"
      >
        <EmailSuggestion
          :id="email"
          v-model.trim="emails[index]"
          data-test-id="forward-email"
          :label="i18n.t('labels.email')"
          :rules="[ruleEmail]"
        />
        <v-btn
          v-if="emails.length > 1"
          class="remove-email-button"
          icon
          @click="removeEmailAtIndex(index)"
          @keydown.enter="removeEmailAtIndex(index)"
          @keydown.space="removeEmailAtIndex(index)"
        >
          <v-icon> fal fa-xmark </v-icon>
        </v-btn>
      </div>
    </v-form>

    <VBtnTertiary
      v-if="AuthService.isAuthenticated"
      class="mb-4 pl-0"
      variant="plain"
      @click="addEmail"
    >
      {{ i18n.t('buttons.add_signatory') }}
    </VBtnTertiary>

    <v-textarea
      v-model="message"
      density="compact"
      hide-details
      :label="i18n.t('labels.add_message')"
      no-resize
      :placeholder="i18n.t('placeholders.add_message')"
      variant="outlined"
    />

    <portal to="document-dialog-actions">
      <VBtnSecondary color="primary" variant="outlined" @click="closeDialog">
        {{ i18n.t('buttons.cancel') }}
      </VBtnSecondary>
      <VBtnPrimary
        color="primary"
        data-test-id="send-invitation"
        :disabled="!formIsValid"
        elevation="0"
        :loading="sendingInvitations"
        @click="sendInvitations"
      >
        {{
          i18n.t(
            emails.length === 1
              ? 'buttons.send_invitation'
              : 'buttons.send_invitations'
          )
        }}
      </VBtnPrimary>
    </portal>
  </template>
</template>
<script setup lang="ts">
import EmailSuggestion from '@/components/inputs/Emailsuggestion/EmailSuggestion.vue';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useSignStore } from '@/stores/sign.ts';
import { cloneDeep } from '@/composables/useUtils';
import { useMessageStore } from '@/stores/message.ts';
import { useEmitter } from '@/composables/useEmitter';
import AuthService from '@/services/authService.ts';
import { useEmailService } from '@/components/inputs/Emailsuggestion/useEmailService';
import { EMAIL_REGEX, ruleEmail } from '@deepcloud/field-validators';

const emit = defineEmits(['closeDialog']);

const i18n = useI18n();
const { saveRecentlyUsedEmails } = useEmailService();
const { currentDocument } = useDocumentContext();
const signStore = useSignStore();
const messageStore = useMessageStore();
const emitter = useEmitter();
// FORM
const forwardForm = ref();
const emails = ref([null]);
const message = ref(null);
const sendingInvitations = ref(false);

// validation
const formIsValid = computed(() => {
  if (emails.value.length < 1) return false;

  let value = true;
  emails.value.forEach((email) => {
    if (email === null) {
      value = false;
    } else {
      if (!EMAIL_REGEX.test(email)) {
        value = false;
      }
    }
  });

  return value;
});

function addEmail() {
  emails.value.push(null);
}

function removeEmailAtIndex(index: number) {
  if (emails.value.length > 1) {
    emails.value.splice(index, 1);
  }
}

function closeDialog() {
  emails.value = [null];
  message.value = null;
  emit('closeDialog');
}

function forwardSignee(email: string) {
  if (currentDocument.value?.documentId) {
    return signStore.forward(currentDocument.value.documentId, {
      email,
      comment: message.value === '' ? null : message.value
    });
  }
}

function addSignee(email: string) {
  if (signStore.document?.signContext.signKey && currentDocument.value) {
    return signStore.addNewSignee(currentDocument.value.documentId, {
      email,
      comment: message.value === '' ? null : message.value
    });
  }
}

async function sendInvitations() {
  const isValid = forwardForm.value.validate();
  const emailsToHandle = cloneDeep(emails.value);

  if (isValid && emailsToHandle.length > 0) {
    sendingInvitations.value = true;

    try {
      // Update preference with new emails
      if (AuthService.isAuthenticated) {
        const emailsToSave = emailsToHandle.map((email) => {
          return {
            email: email ?? ''
          };
        });
        try {
          await saveRecentlyUsedEmails(emailsToSave);
        } catch (error) {
          console.error(error);
        }
      }
      const emailToForward = emailsToHandle.pop();

      // Add all other signees
      const addPromises = emailsToHandle.map((email) => addSignee(email));
      await Promise.all(addPromises);

      // Forward to the remaining email (if there's one)
      if (emailToForward) {
        await forwardSignee(emailToForward);
      }

      messageStore.showMessage({
        key: 'snackbars.signature_forwarded',
        color: 'success',
        icon: 'fa fa-circle-check'
      });
    } catch (error) {
      console.error(error);
    } finally {
      emitter.$emit('update-sign-status');
      closeDialog();
      sendingInvitations.value = false;
    }
  }
}
</script>

<style scoped lang="scss">
.v-input.v-text-field {
  margin-bottom: 0.75rem;
}

.email-input :deep(.slide-y-transition-leave-active) {
  transition: none !important;
}

.email-input :deep(.slide-y-transition-leave-to) {
  transition: none !important;
}

.email-input :deep(.slide-y-transition-enter-active) {
  transition: none !important;
}
.email-input :deep(.slide-y-transition-enter-to) {
  transition: none !important;
}

.remove-email-button {
  margin-bottom: 12px;
}
</style>
